import type { CatalogResponse, MovieSummary, PlaybackLinks, Release } from './types';

export interface PlaybackSource {
  /** 'library' is a moov-first H.264/AAC MP4 by contract, whatever its address. */
  kind: 'hls' | 'library' | 'native';
  url: string;
}

export function hasPlayableAsset(movie: Pick<MovieSummary, 'playback' | 'availability'>): boolean {
  const playback = movie.playback;
  if (!playback) return false;
  return Boolean(
    playback.hls ||
    playback.library ||
    playback.live ||
    playback.native ||
    (movie.availability.playbackReady && playback.progressive),
  );
}

export function isImmediatePlayerRelease(release: Pick<Release, 'playback'>): boolean {
  return release.playback?.native === 'on-the-fly';
}

export function playbackSources(playback: PlaybackLinks | null): PlaybackSource[] {
  if (!playback) return [];

  // Published HLS uses short, independently addressable segments. Keeping it
  // first gives single- and multi-audio titles the same reliable seek path.
  const urls = [
    playback.hls,
    playback.library,
    playback.live,
    playback.progressive,
    playback.hlsMedia,
    playback.native,
  ];
  const seen = new Set<string>();

  return urls.flatMap((url) => {
    if (!url || seen.has(url)) return [];
    seen.add(url);
    if (url === playback.library) return [{ kind: 'library', url } as PlaybackSource];
    return [{ kind: url.includes('.m3u8') ? 'hls' : 'native', url } as PlaybackSource];
  });
}

/** Keep the UI aligned with the concrete playback links returned by the API. */
export function normalizeCatalog(catalog: CatalogResponse): CatalogResponse {
  const available = catalog.available.items.filter(hasPlayableAsset);
  const unavailable = catalog.available.items.filter(
    (movie) =>
      !hasPlayableAsset(movie) && movie.availability.status !== 'failed',
  );
  const upcomingById = new Map(
    [...catalog.upcoming.items, ...unavailable]
      .filter((movie) => movie.availability.status !== 'failed')
      .map((movie) => [movie.id, movie]),
  );
  const upcoming = [...upcomingById.values()];
  const items = [...available, ...upcoming];

  return {
    ...catalog,
    count: items.length,
    available: { count: available.length, items: available },
    upcoming: { count: upcoming.length, items: upcoming },
    items,
  };
}

/** Retry the same authenticated API source locally before switching formats. */
export function withLocalFallbacks(sources: PlaybackSource[]): PlaybackSource[] {
  return sources.flatMap((source) => {
    const absolute = /^[a-z]+:\/\//i.test(source.url);
    const parsed = new URL(source.url, 'https://local.invalid');
    if (parsed.searchParams.get('delivery') === 'local') return [source];
    parsed.searchParams.set('delivery', 'local');
    return [source, { ...source, url: absolute ? parsed.toString() : parsed.pathname + parsed.search + parsed.hash }];
  });
}
